import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api, useAuth } from '../App';
import { trackUpgradeEvent } from '../utils/upgradeTracker';
import Header from '../components/Header';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import { Send, MessageSquare, Search, ArrowLeft, Lock, Sparkles, MapPin, Bell, Paperclip, X, Flag, Loader2 } from 'lucide-react';

export default function Messages() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialUserId = searchParams.get('user');
  
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  // capability = {can_send, reason, tier} returned by /messages/capability.
  // Providers on free tier without an active/completed booking see an
  // upgrade CTA instead of the composer; clients are always can_send=true.
  const [capability, setCapability] = useState(null);
  // When the user taps the "Share booking address" chip we pre-fill the
  // composer and flag the next send so the backend tags it `address_share:
  // true` (skips the PII offender counter and feeds admin telemetry).
  const [addressShareArmed, setAddressShareArmed] = useState(false);
  const [sendingReminder, setSendingReminder] = useState(false);
  // Image attachment state. `pendingImages` holds the compressed Files until
  // the user hits send; `uploadedImageUrls` holds the R2 URLs returned by
  // the upload endpoint; `lightboxImage` drives the full-screen viewer.
  const [pendingImages, setPendingImages] = useState([]);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [lightboxImage, setLightboxImage] = useState(null);
  const fileInputRef = useRef(null);
  const messagesEndRef = useRef(null);

  const fetchConversations = useCallback(async () => {
    try {
      const response = await api.get('/messages/conversations');
      setConversations(response.data || []);
    } catch (error) {
      console.error('Error fetching conversations:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchMessages = useCallback(async (userId) => {
    try {
      const response = await api.get(`/messages?other_user_id=${userId}`);
      setMessages(response.data?.reverse() || []);
      await api.put(`/messages/read/${userId}`);
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  }, []);

  const fetchCapability = useCallback(async (userId) => {
    try {
      const res = await api.get(`/messages/capability?other_user_id=${userId}`);
      setCapability(res.data);
    } catch {
      // On error, fall back to allowing send, the POST itself will re-validate.
      setCapability({ can_send: true, reason: 'ok', tier: null });
    }
  }, []);

  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  useEffect(() => {
    if (selectedConversation) {
      fetchMessages(selectedConversation.user_id);
      fetchCapability(selectedConversation.user_id);
    } else {
      setCapability(null);
    }
  }, [selectedConversation, fetchMessages, fetchCapability]);

  // Funnel: log impression only when capability says the CTA is actually
  // visible to this provider. Avoids inflating the denominator with pro
  // providers or those chatting with booked clients (no CTA shown).
  useEffect(() => {
    if (capability && capability.can_send === false) {
      trackUpgradeEvent('impression', 'messages_upgrade_cta');
    }
  }, [capability]);

  useEffect(() => {
    if (!initialUserId) return;
    // Case 1: we've already loaded conversations and this user is in the list.
    const existing = conversations.find((c) => c.user_id === initialUserId);
    if (existing) {
      setSelectedConversation(existing);
      return;
    }
    // Case 2: no prior chat. Fetch the other user's public info and open an
    // empty conversation so the provider/client can send the first message.
    // Only runs once per initialUserId, guarded by the `selectedConversation`
    // check below so toggling conversations back doesn't retrigger.
    if (selectedConversation?.user_id === initialUserId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get(`/messages/contact?other_user_id=${initialUserId}`);
        if (cancelled) return;
        setSelectedConversation({
          user_id: res.data.user_id,
          name: res.data.name,
          other_user_name: res.data.name,
          last_message: '',
          unread_count: 0,
          // Synthetic flag so the UI can skip zero-message rendering glitches.
          _synthetic: true,
        });
      } catch (err) {
        if (cancelled) return;
        const detail = err.response?.data?.detail;
        toast.error(detail || 'Could not open that conversation.');
      }
    })();
    return () => { cancelled = true; };
  }, [initialUserId, conversations, selectedConversation]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);


  const handleSendMessage = async () => {
    if (!selectedConversation) return;
    if (capability && capability.can_send === false) return;
    const hasText = newMessage.trim().length > 0;
    const hasImages = pendingImages.length > 0;
    if (!hasText && !hasImages) return;

    try {
      let imageUrls = [];
      if (hasImages) {
        setUploadingImages(true);
        const form = new FormData();
        pendingImages.forEach((f) => form.append('files', f));
        const up = await api.post('/messages/upload-images', form, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        imageUrls = (up.data?.images || []).map((i) => i.url);
        setUploadingImages(false);
      }

      const res = await api.post('/messages', {
        receiver_id: selectedConversation.user_id,
        content: newMessage.trim(),
        address_share: addressShareArmed,
        image_urls: imageUrls.length ? imageUrls : undefined,
      });

      if (res?.data?.pii_redacted) {
        toast.info(
          'Some contact details were hidden. Keep chats focused on the service, off-platform contact is not allowed.',
          { duration: 5000 }
        );
      }

      setNewMessage('');
      setAddressShareArmed(false);
      setPendingImages([]);
      fetchMessages(selectedConversation.user_id);
      fetchConversations();
    } catch (error) {
      setUploadingImages(false);
      const detail = error.response?.data?.detail;
      if (error.response?.status === 403 && detail) {
        toast.error(detail);
        fetchCapability(selectedConversation.user_id);
      } else {
        toast.error(detail || 'Failed to send message');
      }
    }
  };

  const handlePickImages = async (e) => {
    const files = Array.from(e.target.files || []);
    // Reset the input so picking the same file twice still fires `change`.
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (!files.length) return;

    // Free providers see the upgrade prompt instead of a silent failure.
    if (capability?.tier === 'free' && user?.role === 'provider' && capability?.can_send) {
      toast.error(
        'Sharing images with new clients is a Pro feature. Upgrade to Pro to send style references.',
        { duration: 5000 },
      );
      return;
    }

    const remainingSlots = 4 - pendingImages.length;
    if (remainingSlots <= 0) {
      toast.error('You can attach up to 4 images per message.');
      return;
    }
    const toProcess = files.slice(0, remainingSlots);
    if (files.length > remainingSlots) {
      toast.info(`Only the first ${remainingSlots} image(s) were added.`);
    }

    // Lazy-import to keep the main bundle lean.
    const { compressImages } = await import('../utils/compressImage');
    const compressed = await compressImages(toProcess);
    setPendingImages((prev) => [...prev, ...compressed].slice(0, 4));
  };

  const handleRemovePendingImage = (idx) => {
    setPendingImages((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleReportImage = async (messageId, imageUrl) => {
    if (!window.confirm('Report this image for review? An admin will be notified.')) return;
    try {
      await api.post('/messages/report-image', {
        message_id: messageId,
        image_url: imageUrl,
      });
      toast.success('Image reported. Thanks for helping keep Zuriya safe.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not report image');
    }
  };

  const handleShareAddress = () => {
    const share = capability?.address_share;
    if (!share?.eligible || !share.address) return;
    const prefill = `Here's my address for ${share.service_name} (Ref ${share.booking_ref}): ${share.address}.`;
    setNewMessage(prefill);
    setAddressShareArmed(true);
  };

  const handleSendReminder = async () => {
    if (!selectedConversation || !capability?.reminder?.allowed) return;
    setSendingReminder(true);
    try {
      await api.post('/messages/reminder', {
        receiver_id: selectedConversation.user_id,
      });
      toast.success('Reminder sent.');
      fetchMessages(selectedConversation.user_id);
      fetchConversations();
      // Re-fetch capability so the button disables (`reminder.sent` becomes true).
      fetchCapability(selectedConversation.user_id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not send reminder');
    } finally {
      setSendingReminder(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const filteredConversations = conversations.filter(c =>
    c.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAFA]">
        <Header />
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#D4AF37]"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Header />
      
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="messages-page">
        <h1 className="font-heading text-3xl font-medium text-[#1A1A1A] mb-6">Messages</h1>
        
        <div className="card-surface overflow-hidden" style={{ height: 'calc(100vh - 200px)' }}>
          <div className="flex h-full">
            {/* Conversation List */}
            <div className={`w-full md:w-80 border-r border-[#E6E4DD] flex flex-col ${selectedConversation ? 'hidden md:flex' : 'flex'}`}>
              <div className="p-4 border-b border-[#E6E4DD]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
                  <Input
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search conversations..."
                    className="pl-10 input-zuriya"
                  />
                </div>
              </div>
              
              <ScrollArea className="flex-1">
                {filteredConversations.length === 0 ? (
                  <div className="p-6 text-center text-stone-500">
                    <MessageSquare className="h-12 w-12 mx-auto mb-3 text-stone-300" />
                    <p>No conversations yet</p>
                  </div>
                ) : (
                  <div className="divide-y divide-[#E6E4DD]">
                    {filteredConversations.map((conv) => (
                      <button
                        key={conv.user_id}
                        onClick={() => setSelectedConversation(conv)}
                        className={`w-full p-4 text-left hover:bg-[#F9F6F0] transition-colors ${
                          selectedConversation?.user_id === conv.user_id ? 'bg-[#F9F6F0]' : ''
                        }`}
                        data-testid={`conversation-${conv.user_id}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 rounded-full bg-[#D4AF37] flex items-center justify-center text-white font-medium flex-shrink-0">
                            {conv.picture ? (
                              <img src={conv.picture} alt={conv.name} className="w-full h-full rounded-full object-cover" />
                            ) : (
                              conv.name?.charAt(0)
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between">
                              <p className={`truncate ${conv.unread_count > 0 ? 'font-semibold text-[#1A1A1A]' : 'font-medium text-[#1A1A1A]'}`}>
                                {conv.name}
                              </p>
                              {conv.unread_count > 0 && (
                                <span className="w-5 h-5 rounded-full bg-red-500 text-white text-xs flex items-center justify-center" data-testid="conversation-unread-dot">
                                  {conv.unread_count}
                                </span>
                              )}
                            </div>
                            <p className={`text-sm truncate ${conv.unread_count > 0 ? 'text-[#1A1A1A] font-medium' : 'text-stone-500'}`}>
                              {conv.last_message}
                            </p>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </div>

            {/* Message Area */}
            <div className={`flex-1 flex flex-col ${selectedConversation ? 'flex' : 'hidden md:flex'}`}>
              {selectedConversation ? (
                <>
                  {/* Chat Header */}
                  <div className="p-4 border-b border-[#E6E4DD] flex items-center gap-3">
                    <button 
                      onClick={() => setSelectedConversation(null)}
                      className="md:hidden p-2 hover:bg-[#F9F6F0] rounded-lg"
                    >
                      <ArrowLeft className="h-5 w-5" />
                    </button>
                    <div className="w-10 h-10 rounded-full bg-[#D4AF37] flex items-center justify-center text-white font-medium">
                      {selectedConversation.picture ? (
                        <img src={selectedConversation.picture} alt={selectedConversation.name} className="w-full h-full rounded-full object-cover" />
                      ) : (
                        selectedConversation.name?.charAt(0)
                      )}
                    </div>
                    <div>
                      <p className="font-medium text-[#1A1A1A]">{selectedConversation.name}</p>
                    </div>
                  </div>

                  {/* Messages */}
                  <ScrollArea className="flex-1 p-4">
                    <div className="space-y-4">
                      {messages.map((msg) => (
                        <div
                          key={msg.message_id}
                          className={`flex ${msg.sender_id === user?.user_id ? 'justify-end' : 'justify-start'}`}
                        >
                          <div className={`max-w-[70%] p-3 rounded-2xl ${
                            msg.sender_id === user?.user_id 
                              ? 'bg-[#D4AF37] text-white rounded-br-none' 
                              : 'bg-[#F9F6F0] text-[#1A1A1A] rounded-bl-none'
                          }`}>
                            {Array.isArray(msg.image_urls) && msg.image_urls.length > 0 && (
                              <div className={`grid gap-1 mb-2 ${
                                msg.image_urls.length === 1 ? 'grid-cols-1' : 'grid-cols-2'
                              }`} data-testid={`message-images-${msg.message_id}`}>
                                {msg.image_urls.map((url, i) => (
                                  <div key={url} className="relative group">
                                    <img
                                      src={url}
                                      alt={`attachment ${i + 1}`}
                                      className="rounded-lg w-full h-auto max-h-56 object-cover cursor-pointer"
                                      onClick={() => setLightboxImage(url)}
                                      loading="lazy"
                                    />
                                    {msg.sender_id !== user?.user_id && (
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleReportImage(msg.message_id, url);
                                        }}
                                        className="absolute top-1 right-1 p-1 rounded-full bg-black/50 text-white opacity-0 group-hover:opacity-100 transition-opacity"
                                        aria-label="Report image"
                                        title="Report image"
                                      >
                                        <Flag className="h-3 w-3" />
                                      </button>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                            {msg.content && <p className="text-sm">{msg.content}</p>}
                            <p className={`text-xs mt-1 ${
                              msg.sender_id === user?.user_id ? 'text-white/70' : 'text-stone-400'
                            }`}>
                              {new Date(msg.created_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false })}
                            </p>
                          </div>
                        </div>
                      ))}
                      <div ref={messagesEndRef} />
                    </div>
                  </ScrollArea>

                  {/* Message Input, Reminder Button, or Upgrade CTA */}
                  {capability?.mode === 'reminder_only' ? (
                    <div
                      className="p-4 border-t border-[#E6E4DD] bg-[#FFF8E7]"
                      data-testid="messaging-reminder-panel"
                    >
                      <div className="flex items-start gap-3 mb-3">
                        <div className="shrink-0 w-10 h-10 rounded-full bg-[#D4AF37]/15 flex items-center justify-center">
                          <Bell className="h-5 w-5 text-[#D4AF37]" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-[#1A1A1A]">
                            {capability.reminder?.sent
                              ? 'Reminder already sent'
                              : 'Booking is pending payment'}
                          </p>
                          <p className="text-xs text-stone-600 mt-0.5">
                            {capability.reminder?.sent
                              ? 'You can send one reminder per booking. Upgrade to Pro for full messaging.'
                              : 'Send a one-time reminder, or upgrade to Pro for full messaging on pending bookings.'}
                          </p>
                        </div>
                      </div>
                      <div className="rounded-lg bg-white border border-[#E6E4DD] p-3 text-xs text-stone-600 mb-3">
                        {capability.reminder?.template}
                      </div>
                      <div className="flex gap-2">
                        <Button
                          onClick={handleSendReminder}
                          disabled={!capability.reminder?.allowed || sendingReminder}
                          className="btn-primary gap-1.5"
                          data-testid="send-reminder-btn"
                        >
                          <Bell className="h-4 w-4" />
                          {capability.reminder?.sent
                            ? 'Reminder sent'
                            : sendingReminder ? 'Sending...' : 'Send reminder'}
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => {
                            trackUpgradeEvent('click', 'messages_reminder_upgrade_cta');
                            navigate('/provider/dashboard?tab=earnings');
                          }}
                          className="gap-1.5"
                          data-testid="reminder-upgrade-btn"
                        >
                          <Sparkles className="h-4 w-4" />
                          Upgrade to Pro
                        </Button>
                      </div>
                    </div>
                  ) : capability && capability.can_send === false ? (
                    <div
                      className="p-4 border-t border-[#E6E4DD] bg-[#FFF8E7]"
                      data-testid="messaging-upgrade-cta"
                    >
                      <div className="flex items-start gap-3">
                        <div className="shrink-0 w-10 h-10 rounded-full bg-[#D4AF37]/15 flex items-center justify-center">
                          <Lock className="h-5 w-5 text-[#D4AF37]" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-[#1A1A1A]">
                            Upgrade to Pro to reply
                          </p>
                          <p className="text-xs text-stone-600 mt-0.5">
                            Free tier providers can reply to clients with an active or completed booking. Upgrade to Pro to message and convert prospects too.
                          </p>
                        </div>
                        <Button
                          onClick={() => {
                            trackUpgradeEvent('click', 'messages_upgrade_cta');
                            navigate('/provider/dashboard?tab=earnings');
                          }}
                          className="btn-primary shrink-0 gap-1.5"
                          data-testid="upgrade-to-pro-btn"
                        >
                          <Sparkles className="h-4 w-4" />
                          Upgrade to Pro
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="p-4 border-t border-[#E6E4DD]">
                      {capability?.address_share?.eligible && (
                        <button
                          type="button"
                          onClick={handleShareAddress}
                          className="mb-3 inline-flex items-center gap-2 text-xs px-3 py-1.5 rounded-full border border-[#D4AF37]/40 bg-[#FFF8E7] text-[#1A1A1A] hover:bg-[#FDEFC4] transition-colors"
                          data-testid="share-booking-address-chip"
                          title={`Share the address from booking ${capability.address_share.booking_ref}`}
                        >
                          <MapPin className="h-3.5 w-3.5 text-[#D4AF37]" />
                          <span className="font-medium">Share booking address</span>
                          <span className="text-stone-500">&middot; Ref {capability.address_share.booking_ref}</span>
                        </button>
                      )}
                      {pendingImages.length > 0 && (
                        <div className="mb-2 flex gap-2 flex-wrap" data-testid="pending-images-tray">
                          {pendingImages.map((f, i) => {
                            const url = URL.createObjectURL(f);
                            return (
                              <div key={`${f.name}-${i}`} className="relative">
                                <img
                                  src={url}
                                  alt={`pending ${i + 1}`}
                                  className="h-16 w-16 object-cover rounded-lg border border-[#E6E4DD]"
                                  onLoad={(e) => URL.revokeObjectURL(e.target.src)}
                                />
                                <button
                                  type="button"
                                  onClick={() => handleRemovePendingImage(i)}
                                  className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-stone-700 text-white flex items-center justify-center hover:bg-stone-900"
                                  aria-label="Remove image"
                                >
                                  <X className="h-3 w-3" />
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      )}
                      <div className="flex gap-2 items-center">
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="image/jpeg,image/jpg,image/png,image/webp,image/gif,image/heic,image/heif"
                          multiple
                          onChange={handlePickImages}
                          className="hidden"
                          data-testid="message-image-input"
                        />
                        <button
                          type="button"
                          onClick={() => fileInputRef.current?.click()}
                          disabled={uploadingImages || pendingImages.length >= 4}
                          className="shrink-0 p-2 rounded-full text-stone-500 hover:text-[#D4AF37] hover:bg-amber-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                          aria-label="Attach images"
                          title="Attach up to 4 images"
                          data-testid="attach-image-btn"
                        >
                          <Paperclip className="h-5 w-5" />
                        </button>
                        <Input
                          value={newMessage}
                          onChange={(e) => setNewMessage(e.target.value)}
                          onKeyPress={handleKeyPress}
                          placeholder="Type a message..."
                          className="input-zuriya"
                          data-testid="message-input"
                        />
                        <Button
                          onClick={handleSendMessage}
                          disabled={uploadingImages || (!newMessage.trim() && pendingImages.length === 0)}
                          className="btn-primary"
                          data-testid="send-message-btn"
                        >
                          {uploadingImages ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                        </Button>
                      </div>
                    </div>
                  )}
                  {/* Lightbox for full-screen image viewer */}
                  {lightboxImage && (
                    <div
                      className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
                      onClick={() => setLightboxImage(null)}
                      data-testid="image-lightbox"
                    >
                      <button
                        type="button"
                        onClick={() => setLightboxImage(null)}
                        className="absolute top-4 right-4 text-white/80 hover:text-white"
                        aria-label="Close image"
                      >
                        <X className="h-6 w-6" />
                      </button>
                      <img
                        src={lightboxImage}
                        alt="attachment"
                        className="max-h-full max-w-full rounded-lg shadow-lg"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                  )}
                </>
              ) : (
                <div className="flex-1 flex items-center justify-center text-center p-6">
                  <div>
                    <MessageSquare className="h-16 w-16 text-stone-300 mx-auto mb-4" />
                    <h3 className="font-heading text-xl font-medium text-[#1A1A1A] mb-2">
                      Select a conversation
                    </h3>
                    <p className="text-stone-500">
                      Choose a conversation from the list to start messaging
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
